/**
 * Mixin de `APIChat` (`apiChat.js`) — handlers dos eventos SSE do Modo Agente
 * (spec-002-chat-agentico-estilo-claude-code, `plan.md` D13).
 *
 * Extraído de `apiChatModoAgente.js` por tamanho (1.4.9 — `CLAUDE.md` raiz:
 * arquivos ≤ 500 linhas; aquele arquivo cruzou o teto com `getRun`/`retryRun`
 * do G-D6). Contém só os handlers despachados por `getEventHandlers`
 * (`apiChatStream.js`) e a hidratação de `propostasAbertas` que tem o MESMO
 * shape de `_onToolApprovalRequest` — o ciclo de vida do run (aceitar/
 * cancelar/reidratar/cursor) fica no arquivo principal.
 */
import store from '@state/store'
import { propostaDoRelatorio } from '@modules/aawz-intelligence-v2/utils/desfecho-da-proposta'
import { valorSinteticoDaProposta } from '@modules/aawz-intelligence-v2/api/valor-do-cartao-de-proposta'
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'
import { runDaConversa } from '@modules/aawz-intelligence-v2/utils/run-da-conversa'
const ESTADO_APROVACAO = TOOL_PART_STATE.APROVACAO
// Os mesmos quatro de `apiChatModoAgente.js::ESTADOS_TERMINAIS_RUN` (duplicados
// pelo mesmo motivo do `STORE` abaixo: sem import circular entre os mixins).
const ESTADOS_TERMINAIS_DO_RUN = new Set(['done', 'failed', 'cancelled', 'expired'])


// Mesma STORE fixa de `apiChatModoAgente.js` — duplicada de propósito (é uma
// string constante, nunca diverge) pra não criar import circular entre os
// dois mixins.
const STORE = 'aawz-intelligence-v2'
// O prefixo do namespace de uma CONVERSA VIVA (`store/conversa-viva.js` ->
// `PREFIXO_NAMESPACE`). Duplicado pelo MESMO motivo do `STORE` acima:
// `store/conversa-viva.js` importa `store/actions.js`, que importa `api/*` —
// puxa-lo daqui fecharia um ciclo. O drift tem teste (`apiChat.plano.unit.js`).
const PREFIXO_CONVERSA_VIVA = 'iaConversa:'

export const apiChatModoAgenteEventosMixin = {
  /**
   * O slot `runAtual` do store FIXO ainda é do run que ESTA instância
   * acompanha?
   *
   * ★ [review #2364] Com as conversas em ABAS há um motor por aba e UM slot
   * para todos: o `setRunAtual` da aba B substitui o run da A, e os eventos
   * `estado`/`seq` que ainda chegam à instância da A escreviam por cima do
   * run da B — o chip "Falhou" do cancelamento da A aparecia na B (o sintoma
   * do owner, invertido). Escrita no slot só quando ele é do MEU run; sem
   * run no slot, ou sem run acompanhado aqui, nada a proteger (canal legado).
   *
   * @returns {boolean}
   */
  _slotDoRunEhMeu() {
    const getters = store.getters || {}
    const noSlot = getters[STORE + '/runAtual']
    const idNoSlot = noSlot && noSlot.id_run
    if (!idNoSlot || !this._runAtualIdRun) return true
    return idNoSlot === this._runAtualIdRun
  },

  /**
   * `estado` (US-13) — transição do run (`queued|running|awaiting_approval|
   * done|failed|cancelled|expired`), exibida no chip de `run-status-chip.vue`.
   *
   * @param {Object} data Payload do evento SSE `estado`.
   */
  _onEventEstado(data) {
    if (!data) return
    if (!this._slotDoRunEhMeu()) return
    // [1.4.9/G-D6c] `retry_permitido` (contrato events.json §estado) é a
    // ÚNICA fonte de "pode tentar de novo" do chip — `run-atual` (reidratação)
    // não carrega esse campo, então some depois de um reload de um run
    // `failed` até chegar um evento `estado` novo (limitação aceita).
    // [VERIFY-3b/G-D17] `efeitos_conhecidos` (Recibo[]) também viaja em TODA
    // transição `estado` (não só cancelamento) — o chip só os exibe em
    // `cancelled` (`run-status-chip.vue::mostrarEfeitos`).
    this._dispatchNoStoreDoAgente('setRunEstado', {
      estado: data.para || null, motivo: data.motivo || null, retry_permitido: data.retry_permitido,
      efeitos_conhecidos: data.efeitos_conhecidos,
    })
  },

  /**
   * `plano` — TodoWrite/TodoListMiddleware do turno em andamento. Pode repetir
   * no mesmo turno; a mutation `SET_PLANO` só substitui pela versão MAIOR.
   * Também guarda localmente (`this._planoAtual`) para persistir em
   * `message.plano` no `_onEventEnd` — mesmo padrão de `this._painel`
   * (`chat-message.vue::plano` lê `message.plano` fora do streaming).
   *
   * @param {Object} data Payload do evento SSE `plano`.
   */
  _onEventPlano(data) {
    // ★ [merge 21/09/2026] A MESMA régua de `apiChat.js::_onPlano`, que
    // divide este evento com este handler (`chat-event-handlers.js`): sem
    // `versao` numérica não há como ordenar, e a mutation não seria alcançada
    // — despachar aqui mandaria `versao: 0` ao store à toa.
    if (!data || typeof data.versao !== 'number') return
    const versaoAtual = (this._planoAtual && this._planoAtual.versao) || 0
    const versaoNova = data.versao
    // [VERIFY-3h, SHOULD-1 review-frontend-pr2242] Estrito (`>`, não `>=`) —
    // "substitui pela versão MAIOR" (contracts/events.json) é comparação
    // ESTRITA; `>=` deixava um replay/reenvio da MESMA versão (conteúdo
    // divergente, ex. corrida do backend) sobrescrever o que já estava na
    // tela sem necessidade. `store/mutations.js::SET_PLANO` tem o mesmo
    // guard, tem que mudar junto.
    // Versão igual ou menor não é evento novo: nem o local nem o store mudam
    // (o `SET_PLANO` tem o mesmo guard — o despacho a mais era ruído).
    if (this._planoAtual && versaoNova <= versaoAtual) return
    this._planoAtual = { itens: data.itens || [], versao: versaoNova }
    this._dispatchNoCanalDoPlano('setPlano', { itens: data.itens || [], versao: versaoNova })
  },

  /**
   * `decisao` — fecha o card da proposta em TODAS as abas (a decisão pode ter
   * vindo de outra aba/sessão do mesmo profissional).
   *
   * @param {Object} data Payload do evento SSE `decisao`.
   */
  _onEventDecisao(data) {
    if (!data || !data.id_proposta) return
    this._dispatchNoStoreDoAgente('resolverProposta', {
      id_proposta: data.id_proposta, resultado: data.resultado, recibo: data.recibo || null,
    })
  },

  /**
   * `tool-approval-request` (fio) — renomeação pura de `confirmacao` (US-8):
   * o backend NÃO manda os dois, então este é o único ponto de entrada do
   * card de proposta. Faz DOIS dispatches:
   *   1. `upsertProposta` no mapa `propostasAbertas` (STORE fixo — [adv C-25],
   *      N cards e N decisões independentes).
   *   2. `setPendingConfirmation` com `id_proposta` (`this.storeNamespace`,
   *      mesmo canal do preview legado): é o campo que `messages.vue::
   *      propostaAtual`/`showLegacyConfirmAction` usa pra NÃO duplicar o
   *      card legado quando a mesma proposta já está no `v-for` de
   *      `propostasParaExibir` — sem ele, os dois cards (legado + novo)
   *      renderizariam juntos pra uma proposta só (plan.md D13).
   *
   * @param {Object} data Payload do evento SSE `tool-approval-request`.
   */
  // ★ merge PR #2242 — este método se chamava `_onToolApprovalRequest` e, pelo
  // `Object.assign`, SOBRESCREVIA o da classe: o de lá cria a PARTE do turno
  // (`upsertToolPart`, `state: approval-requested`) que faz o cartão do
  // registry nascer na bolha. Os dois tratam o MESMO evento e fazem coisas
  // diferentes — o de lá desenha, este alimenta o mapa de propostas que o Modo
  // Agente decide/expira. Renomeado para que os DOIS rodem: quem chama é o
  // `_onToolApprovalRequest` da classe, no fim dele.
  _registrarPropostaDoModoAgente(data) {
    if (!data || !data.id_proposta) return
    this._dispatchNoStoreDoAgente('upsertProposta', {
      id_proposta: data.id_proposta,
      hash_payload: data.hash_payload || null,
      expira_em: data.expira_em || null,
      estado: 'aberta',
      preview: {
        tool: data.tool || null,
        entidade: data.entidade || null,
        operacao: data.operacao || null,
        alvo: data.alvo || null,
        resumo: data.resumo || null,
      },
    })
    store.dispatch(this.storeNamespace + '/setPendingConfirmation', {
      tool: data.tool || '',
      resumo: data.resumo || '',
      entidade: data.entidade || '',
      operacao: data.operacao || '',
      resolved: '',
      id_proposta: data.id_proposta,
    })
  },

  /**
   * Reidrata `propostasAbertas` (mapa por `id_proposta`) a partir do retorno
   * de `GET /conversas/{id}/run-atual` — cards abertos sobrevivem a
   * reload/nova aba ([adv C-24]).
   *
   * [VERIFY-1/C-1] Repassa `hash_payload`/`preview` (mesmo shape de
   * `_onToolApprovalRequest`) — sem eles, o card reidratado não teria como
   * decidir (POST de decisão exige `hash_payload`) nem mostrar título/resumo.
   * Depende do backend devolver esses campos em `propostas_abertas`
   * (contrato 1.4.7, dev-T050); campos ausentes chegam `null` e o card
   * degrada como hoje (sem quebrar).
   *
   * ⚠️ Esta lista é só o que ainda pode ser DECIDIDO. O que já aconteceu vem
   * em `value.propostas` e entra por `_aplicarRelatorioDoRun` — ver lá.
   *
   * @param {Array=} lista `value.propostas_abertas` do envelope.
   */
  _aplicarPropostasAbertas(lista) {
    if (!Array.isArray(lista)) return
    for (const p of lista) {
      if (!p || !p.id_proposta) continue
      this._dispatchNoStoreDoAgente('upsertProposta', {
        id_proposta: p.id_proposta,
        hash_payload: p.hash_payload || null,
        expira_em: p.expira_em || null,
        estado: 'aberta',
        preview: {
          tool: p.tool || null,
          entidade: p.entidade || null,
          operacao: p.operacao || null,
          alvo: p.alvo || null,
          resumo: p.resumo || null,
        },
      })
      this._reidratarParteDoCartao(p)
    }
  },

  /**
   * ★ [A-147] Recria a PARTE do cartão de confirmação a partir do snapshot.
   *
   * Medido em produção (19/09/2026, run `b0a72965`): com a proposta ABERTA no
   * mapa e `runAtual.estado === 'awaiting_approval'`, a tela ficava sem cartão
   * — chip anunciando "1 proposta aberta" e nada para decidir. Causa: quem
   * desenha o cartão é o REGISTRY, a partir da parte de tool
   * (`toolParts`/`crm/confirmacao`), e o mapa `propostasAbertas` que esta
   * função repunha não é consumido por tela nenhuma. A parte se perde em três
   * caminhos reais — o descarte do turno na desmontagem da view, o
   * `clearToolParts` do começo de turno e a reconexão por CURSOR (o evento que
   * cria a parte fica atrás do cursor e o replay não o reenvia).
   *
   * O `toolCallId` vem do snapshot (`referencia_worker` da proposta, campo
   * aditivo do backend) e é o MESMO do evento `tool-approval-request`, então
   * um replay posterior faz upsert na mesma parte — nunca um segundo cartão.
   * Sem ele (backend antigo), a função não inventa chave: só o mapa é
   * reidratado, como antes.
   */
  _reidratarParteDoCartao(p) {
    if (!p.toolCallId) return
    const parte = {
      toolCallId: p.toolCallId,
      state: ESTADO_APROVACAO,
      tool: p.tool || '',
      resumo: p.resumo || '',
      entidade: p.entidade || '',
      operacao: p.operacao || '',
      id_proposta: p.id_proposta,
      // ★ A MESMA montagem do caminho do evento (`valor-do-cartao-de-proposta`,
      // fonte única). A primeira versão desta função inventou o formato — pôs
      // os campos dentro de `preview` — e o `validar.js` do registry reprovava
      // (`props.resumo: required` … `props.operacao_label: required`): na tela,
      // "Não consegui montar este cartão · dados fora do contrato", que é o
      // sintoma que este fix existe para acabar. Pego pela junção contra o
      // payload REAL do run `b592d381`.
      value: valorSinteticoDaProposta(p),
    }
    store.dispatch(this.storeNamespace + '/upsertToolPart', parte)
  },

  /**
   * [T313 / R3-4, spec-003 US-8 AC3] Reidrata o RELATÓRIO do run — `value.
   * propostas` de `GET /conversas/{id}/run-atual`.
   *
   * `propostas_abertas` diz o que ainda pode ser decidido; esta lista diz o
   * que ACONTECEU, por ação, com a decisão e o desfecho de cada uma. Ela é o
   * que faz a AC3 ("persistir esse relatório junto ao `id_run`, recuperável
   * depois do turno") chegar à tela: a trilha existia em `run_decisoes` desde
   * a v661, o backend passou a lê-la, e sem esta ponte ela chegava aqui e era
   * descartada — o gestor dava F5 depois do lote e perdia justamente as
   * linhas que dizem o que NÃO foi feito.
   *
   * Aplicada ANTES de `_aplicarPropostasAbertas`: as duas listas se
   * sobrepõem nas propostas ainda abertas, e o `UPSERT_PROPOSTA` funde as
   * entradas — quem vale para essas é a lista das abertas, que é a decisão do
   * servidor sobre o que ainda aceita decisão (em run terminal ela vem vazia
   * de propósito, mesmo havendo proposta `aberta` no relatório).
   *
   * @param {Array=} lista `value.propostas` do envelope.
   */
  _aplicarRelatorioDoRun(lista) {
    if (!Array.isArray(lista)) return
    for (const linha of lista) {
      if (!linha || !linha.id_proposta) continue
      this._dispatchNoStoreDoAgente('upsertProposta', propostaDoRelatorio(linha))
    }
  },

  // Canais do Modo Agente que só registram para 001/003 renderizarem —
  // mesma razão do painel legado: STORE fixo, e não `this.storeNamespace`
  // (uma superfície com namespace próprio, ex. Fee Fixo, roda um módulo Vuex
  // TOTALMENTE diferente — dispatch aqui levantaria "unknown action type").
  _resetarCanaisDoAgente() {
    // A action AGREGADORA, nunca a lista solta: os quatro canais são zerados
    // de três lugares, e com a lista repetida o quinto canal é esquecido em
    // dois deles (é o comentário dela em `store/actions.js`, medido em 17/09).
    store.dispatch(STORE + '/resetCanaisDoAgente')
    this._soltarRunTerminalDoSlot()
    // O `plano` mora no namespace da CONVERSA (ver `_namespaceDoPlano`), e o
    // reset acima so alcanca o store fixo: sem este segundo reset, o plano do
    // turno ANTERIOR sobreviveria na aba e a encenacao do turno novo comecaria
    // com a fila do velho.
    const nsDoPlano = this._namespaceDoPlano()
    if (nsDoPlano !== STORE) store.dispatch(nsDoPlano + '/resetPlano')
  },

  /**
   * ★ [owner, 21/09/2026] Turno novo não herda run VELHO. Depois de um F5 a
   * reidratação (`runAtual(idConversa)`) deixa no slot fixo o ÚLTIMO run da
   * conversa, mesmo já terminado (`cancelled`/`failed`/`done`/`expired`) — e
   * `messages.vue::showStreamingMessage` esconde a casca "Pensando..." com um
   * run `cancelled` no slot. Medido pelo owner: F5 numa conversa, mensagem
   * nova, a degradação para o Modo Chat avisa, e o "Pensando..." só aparece
   * junto com a resposta pronta. Aqui, no início de cada turno, o run
   * terminal DESTA conversa sai do slot; o run VIVO (de qualquer aba) fica.
   */
  _soltarRunTerminalDoSlot() {
    const getters = store.getters || {}
    const noSlot = getters[STORE + '/runAtual']
    // O estado primeiro, a conversa depois: só um run terminal justifica ler
    // `selectedConversation` aqui (o turno já a lê no `start`).
    if (!noSlot || !ESTADOS_TERMINAIS_DO_RUN.has(noSlot.estado)) return
    if (!runDaConversa(noSlot, this._conversaAtual())) return
    this._dispatchNoStoreDoAgente('resetRunAtual')
  },

  /**
   * ★ merge PR #2242 — dedupe de replay parcial de grupo dual (C-78), vindo do
   * `apiChatStream.js` (o mixin que o merge deixou de aplicar — 11 dos 15
   * métodos dele colidiam com a classe, que `desenvolvimento` evoluiu).
   *
   * Watermark por `(seq, frameIndex)`: reconectar no meio de um grupo (o cursor
   * só avança no ÚLTIMO frame) reenvia o grupo INTEIRO, e sem isto o que o
   * primeiro frame já aplicou entra de novo — token duplicado, tool_call
   * duplicado. Evento sem `seq` (Modo Chat) nunca é considerado duplicado.
   */
  _frameJaAplicado(seq, frameIndex) {
    if (seq === null || seq === undefined) return false
    const ultimo = this._ultimoFrameAplicado
    if (!ultimo) return false
    if (seq < ultimo.seq) return true
    const indiceAtual = (frameIndex === null || frameIndex === undefined) ? 0 : frameIndex
    return seq === ultimo.seq && indiceAtual <= ultimo.frameIndex
  },

  _marcarFrameAplicado(seq, frameIndex) {
    if (seq === null || seq === undefined) return
    this._ultimoFrameAplicado = {
      seq: seq,
      frameIndex: (frameIndex === null || frameIndex === undefined) ? 0 : frameIndex,
    }
  },

  /**
   * [T101, spec-001 `pesquisa-internet-chat`] Os três canais transientes do
   * turno que acabou, prontos para viajar na mensagem persistida.
   *
   * ★ Por que a mensagem precisa deles. A bolha EM STREAMING some no `end` e é
   * substituída pela bolha persistida, que nasce do `addMessage`. Sem este
   * carimbo, as fontes da busca apareceriam durante o turno e sumiriam no
   * instante exato em que o consultor termina de ler a resposta — a US-6
   * valeria só enquanto ninguém estivesse lendo.
   *
   * ⚠️ E vale só DENTRO da sessão: `GET /messages` não devolve estes campos
   * (nem devolve `plano`, que tem a mesma limitação desde a spec-002). Depois
   * de um F5 os blocos não voltam — o edge case "conversa recarregada mantém
   * os cartões" da US-6 continua **não coberto**, e fechá-lo é trabalho do
   * backend, não daqui.
   *
   * Lê do STORE FIXO, e não de `this.storeNamespace`, pela mesma razão de
   * `_resetarCanaisDoAgente`: uma superfície com namespace próprio roda um
   * módulo Vuex sem estes getters.
   *
   * @returns {Object} `{ busca, codigo, arquivo }` — arrays, nunca `undefined`.
   */
  _canaisDoAgenteDoTurno() {
    // `store.getters || {}`: o `_onEventEnd` é o ÚNICO ponto em que o turno
    // vira mensagem. Um `TypeError` aqui não perderia três blocos — perderia a
    // RESPOSTA inteira, e em silêncio (o handler roda dentro do laço do
    // stream). Um store sem `getters` é anômalo; degradar para "sem canais" é
    // a falha certa.
    const getters = store.getters || {}
    return {
      busca: getters[STORE + '/buscaStreaming'] || [],
      codigo: getters[STORE + '/codigoStreaming'] || [],
      arquivo: getters[STORE + '/arquivoStreaming'] || [],
    }
  },

  /**
   * Dispatch no STORE FIXO do módulo principal — canais do Modo Agente.
   *
   * ★ O canal que não é acompanhado pelo motor (`_acompanhaRunPeloMotor()`
   * false — hoje só o agent-chat) não escreve aqui: o STORE acima é o do
   * `aawz-intelligence-v2`, e despachar por ele seria gravar estado de run na
   * store de OUTRO módulo. Mesma razão do `id_run` do `start` não armar
   * reconexão (`apiChat.js::_fixarConversaERunDoStart`); a guarda fica no
   * helper para fechar a família toda, não só aquele campo.
   */
  _dispatchNoStoreDoAgente(action, payload) {
    store.dispatch(STORE + '/' + action, payload)
  },

  /**
   * O namespace do canal `plano` — a CONVERSA, e nao o store fixo.
   *
   * ★ POR QUE ESTE CANAL E O UNICO QUE SAI DO STORE FIXO (20/09/2026)
   *
   * Os outros canais do agente (`runAtual`, `propostasAbertas`,
   * `reconectando`) sao escritos e lidos no store FIXO pelos mesmos tres
   * consumidores (`messages.vue`, `view-chat-modo-agente.mixin.js` e
   * `crm-confirmacao-card.vue`) — produtor e leitor concordam, e o cartao de
   * confirmacao nem recebe `storeModule` para poder divergir.
   *
   * O `plano` nao: quem o le e o CARTAO DA CONVERSA
   * (`messages-painel-do-chat-mixin.js` -> `this.storeModule + '/plano'`), que
   * na Home IA e o namespace da conversa viva (`iaConversa:<uuid>`). Escrito
   * no fixo, ele NUNCA chegava la — e e esse cartao quem ALIMENTA o relogio
   * da encenacao (`utils/encenacao-do-plano.js`). Sem relogio alimentado,
   * `encenacaoDrenando()` e sempre `false`: o progresso nao aparecia etapa a
   * etapa, o texto e os cartoes deixavam de esperar por ele, e a abertura
   * automatica do painel (`renderiza-partes.js::esperarEncenacaoDoPlano`)
   * perdia o adiamento. Medido na tela em 20/09/2026 (owner: *"antes tinhamos
   * a enganacao no front com o componente de artefato no progresso indo aos
   * poucos... atualmente isso nao acontece"*).
   *
   * ⚠️ Namespace de OUTRA superficie (Fee Fixo, Comissoes) NAO entra aqui: o
   * modulo Vuex de la nao tem `setPlano`/`resetPlano`, e o dispatch levantaria
   * "unknown action type". Para elas o canal continua no store fixo, byte a
   * byte como antes.
   */
  _namespaceDoPlano() {
    const ns = this.storeNamespace
    if (ns === STORE) return STORE
    return this._ehNamespaceDeConversaViva() ? ns : STORE
  },

  /**
   * O namespace desta instância é o de uma CONVERSA VIVA (`iaConversa:<uuid>`,
   * `store/conversa-viva.js::namespaceDaConversa`)?
   *
   * A régua é uma só, e mora aqui para o canal do `plano` e os canais do
   * TURNO (`apiChat.js::_namespaceTemCanaisDoTurno` — fase e nó do
   * Supervisor) não divergirem no dia em que o prefixo mudar. O drift do
   * prefixo contra a fábrica tem teste (`apiChat.plano.unit.js`).
   *
   * @returns {boolean}
   */
  _ehNamespaceDeConversaViva() {
    const ns = this.storeNamespace
    return typeof ns === 'string' && ns.indexOf(PREFIXO_CONVERSA_VIVA) === 0
  },

  /** Mesma guarda de `_dispatchNoStoreDoAgente`, no namespace da conversa. */
  _dispatchNoCanalDoPlano(action, payload) {
    if (!this._acompanhaRunPeloMotor()) return
    store.dispatch(this._namespaceDoPlano() + '/' + action, payload)
  },

  /**
   * ★ [spec-002 T051b/MUST-1, review PR #2328] Guarda o evento `busca` NA
   * INSTÂNCIA — o mesmo padrão de `_planoDoTurno`, e pelo MESMO motivo: o canal
   * `buscaStreaming` é STORE FIXO e compartilhado entre superfícies
   * (`_dispatchNoStoreDoAgente`), então ler o store no `end` arriscaria misturar
   * a busca de OUTRA conversa/instância. Aqui é por instância, sem esse risco.
   *
   * O carimbo de `coletado_em` espelha `ADICIONAR_BUSCA_STREAMING`
   * (`store/mutations.js`) — mesma regra, duas gravações (instância + store)
   * do MESMO evento, nunca duas fontes de verdade sobre o mesmo carimbo:
   * quem chegou primeiro no evento (o `coletado_em` que ele já trouxer) vence.
   *
   * @param {Object} data Payload do evento SSE `busca`.
   */
  _registrarBuscaWebDoTurno(data) {
    if (!data) return
    const comCarimbo = data.coletado_em ? data : { ...data, coletado_em: new Date().toISOString() }
    this._buscaWebDoTurno = (this._buscaWebDoTurno || []).concat([comCarimbo])
  },
}
